import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Users, Plus, Pencil, Trash2, MapPin, Mail, Phone, Search, X, Save, Loader2, Check, ChevronDown, ChevronUp, Share2, Camera } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { CJ_AVATAR_URL, COUNTRIES, US_STATES } from "../lib/constants";
import { toast } from "sonner";

const empty = {
  name: "",
  email: "",
  phone: "",
  city: "",
  state: "",
  country: "",
  bio: "",
  specialties: [],
  languages: [],
  available: true,
};

export default function Facilitators() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "super_admin";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("");
  const [state, setState] = useState("");
  const [editing, setEditing] = useState(null); // facilitator id, "new", or null
  const [searchParams, setSearchParams] = useSearchParams();
  // Capture the ?id= param into local state so the highlight/scroll survives
  // after we strip the URL param. We watch searchParams so same-route
  // navigations (e.g. palette click while already on /facilitators) also
  // trigger a fresh focus, not just the initial mount.
  const [focusId, setFocusId] = useState(() => searchParams.get("id"));
  useEffect(() => {
    const wanted = searchParams.get("id");
    if (wanted && wanted !== focusId) setFocusId(wanted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    // After the list loads with a focus id, strip the query param so the
    // param doesn't stick around across future navigations.
    if (focusId && list.length > 0) {
      const still = new URLSearchParams(searchParams);
      still.delete("id");
      setSearchParams(still, { replace: true });
      // Clear the highlight after a beat so it isn't permanent.
      const t = setTimeout(() => setFocusId(null), 2500);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.length]);

  const load = () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (country) params.country = country;
    if (state) params.state = state;
    api.get("/facilitators", { params })
      .then(({ data }) => setList(Array.isArray(data) ? data : []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, country, state]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-12">
      <Link to="/" data-testid="facilitators-back" className="inline-flex items-center gap-1 text-sm font-bold text-slate-500 hover:text-tc-ink">
        <ArrowLeft size={14} /> Back to Hub
      </Link>

      <div className="mt-4 flex items-start gap-4 flex-wrap">
        <img src={CJ_AVATAR_URL} alt="CJ" className="h-16 w-16 rounded-full object-cover shadow-md ring-2 ring-white shrink-0" />
        <div className="flex-1 min-w-[240px]">
          <div className="tc-eyebrow text-[#7ac143]">Facilitator Directory</div>
          <h1 className="font-display text-3xl sm:text-4xl text-tc-ink leading-tight">Who to call when you book an experience.</h1>
          <p className="text-slate-500 mt-1.5 text-sm max-w-xl">Search by location, language, or specialty. {canEdit ? "Tap a card to edit." : "Talk to admin to update."}</p>
        </div>
        {canEdit && (
          <button
            onClick={() => setEditing("new")}
            data-testid="facilitators-add"
            className="h-11 px-4 rounded-2xl text-white font-bold flex items-center gap-2 shadow-md hover:-translate-y-0.5 transition-all"
            style={{ background: "#7ac143" }}
          >
            <Plus size={16} /> Add facilitator
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="mt-6 grid sm:grid-cols-3 gap-3">
        <div className="relative sm:col-span-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, city, specialty…" data-testid="facilitators-search" className="w-full h-11 rounded-xl bg-white border-2 border-slate-200 focus:border-purple-500 outline-none pl-10 pr-9 text-sm" />
          {search && <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-100 text-slate-400"><X size={12} /></button>}
        </div>
        <select value={country} onChange={(e) => { setCountry(e.target.value); if (e.target.value !== "United States") setState(""); }} data-testid="facilitators-country" className="h-11 rounded-xl bg-white border-2 border-slate-200 focus:border-purple-500 outline-none px-3 text-sm font-medium">
          <option value="">All countries</option>
          {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
        </select>
        {country === "United States" ? (
          <select value={state} onChange={(e) => setState(e.target.value)} data-testid="facilitators-state" className="h-11 rounded-xl bg-white border-2 border-slate-200 focus:border-purple-500 outline-none px-3 text-sm font-medium">
            <option value="">All states</option>
            {US_STATES.map((s) => <option key={s}>{s}</option>)}
          </select>
        ) : (
          <input value={state} onChange={(e) => setState(e.target.value)} placeholder="Region (optional)" data-testid="facilitators-state-text" className="h-11 rounded-xl bg-white border-2 border-slate-200 focus:border-purple-500 outline-none px-3 text-sm" />
        )}
      </div>

      {/* List */}
      <div className="mt-6">
        {loading ? (
          <div className="text-slate-400 py-12 text-center">Loading…</div>
        ) : list.length === 0 ? (
          <div className="tc-card p-10 text-center text-slate-500" data-testid="facilitators-empty">
            <Users size={36} className="mx-auto text-slate-300" />
            <div className="font-display text-2xl text-tc-ink mt-3">No facilitators yet.</div>
            <div className="text-sm mt-1">{canEdit ? "Tap 'Add facilitator' to start building the directory." : "Once admins add facilitators, you'll see them here."}</div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="facilitators-grid">
            {(Array.isArray(list) ? list : []).map((f, i) => <Card key={f.id} f={f} canEdit={canEdit} onEdit={() => setEditing(f.id)} delay={i * 0.03} focused={focusId === f.id} />)}
          </div>
        )}
      </div>

      <AnimatePresence>
        {editing && (
          <EditModal
            id={editing}
            existing={editing === "new" ? null : list.find((x) => x.id === editing)}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); load(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function Card({ f, canEdit, onEdit, delay, focused }) {
  const [expanded, setExpanded] = useState(!!focused);
  const cardRef = useRef(null);

  // When the palette focuses this row, scroll it into view and highlight
  // for a moment so the user knows why the page loaded here.
  useEffect(() => {
    if (focused && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      setExpanded(true);
    }
  }, [focused]);
  // Anything longer than this many chars gets collapsed by default.
  const COLLAPSE_THRESHOLD = 260;
  const bio = f.bio || "";
  const isLong = bio.length > COLLAPSE_THRESHOLD;
  const displayedBio = !isLong || expanded ? bio : bio.slice(0, COLLAPSE_THRESHOLD).trimEnd() + "…";

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, type: "spring", stiffness: 140, damping: 18 }}
      className={`tc-card p-5 group relative flex flex-col ${focused ? "ring-2 ring-offset-2 ring-orange-400" : ""}`}
      style={{ scrollMarginTop: 96 }}
      data-testid={`facilitator-card-${f.id}`}
    >
      {!f.available && (
        <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest font-bold bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">Unavailable</span>
      )}
      <div className="flex items-start gap-3">
        {f.photo_path ? (
          <div
            className="w-16 h-16 rounded-full bg-cover bg-center shrink-0 ring-2 ring-white shadow-md border-2 border-[#7ac143]/30"
            style={{ backgroundImage: `url(${fileUrl(f.photo_path)})` }}
            data-testid={`facilitator-photo-${f.id}`}
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-[#7ac143]/15 flex items-center justify-center text-[#7ac143] font-display text-xl shrink-0 ring-2 ring-white shadow-sm">
            {f.name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl text-tc-ink leading-tight truncate">{f.name}</div>
          {(f.city || f.state || f.country) && (
            <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
              <MapPin size={12} /> <span className="truncate">{[f.city, f.state, f.country].filter(Boolean).join(", ")}</span>
            </div>
          )}
        </div>
      </div>
      {bio && (
        <>
          <p className="text-sm text-slate-600 mt-3 leading-relaxed whitespace-pre-line" data-testid={`facilitator-bio-${f.id}`}>
            {displayedBio}
          </p>
          {isLong && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              data-testid={`facilitator-bio-toggle-${f.id}`}
              className="self-start mt-2 inline-flex items-center gap-1 text-xs font-bold text-[#0091be] hover:text-[#006a8c] focus:outline-none"
            >
              {expanded ? <>Show less <ChevronUp size={12} /></> : <>Read full bio <ChevronDown size={12} /></>}
            </button>
          )}
        </>
      )}
      {f.specialties?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-3">
          {f.specialties.slice(0, 4).map((s) => <span key={s} className="tc-pill bg-[#7ac143]/10 text-[#3f6e1c] text-[10px]">{s}</span>)}
        </div>
      )}
      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center gap-3 text-xs text-slate-500">
        {f.email && <a href={`mailto:${f.email}`} className="inline-flex items-center gap-1 hover:text-tc-ink"><Mail size={12} /> Email</a>}
        {f.phone && <a href={`tel:${f.phone}`} className="inline-flex items-center gap-1 hover:text-tc-ink"><Phone size={12} /> Call</a>}
        <a
          href={`/facilitators/${f.id}/bio`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`facilitator-share-${f.id}`}
          className="inline-flex items-center gap-1 hover:text-[#0091be] font-medium"
        >
          <Share2 size={12} /> Share
        </a>
        {canEdit && (
          <button onClick={onEdit} data-testid={`facilitator-edit-${f.id}`} className="ml-auto inline-flex items-center gap-1 font-bold text-[#7ac143] hover:text-[#5a9c2f]">
            <Pencil size={12} /> Edit
          </button>
        )}
      </div>
    </motion.div>
  );
}

function PhotoPicker({ facilitatorId, path, onChange, uploading, setUploading }) {
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/facilitators/photo", fd);
      if (facilitatorId) await api.put(`/facilitators/${facilitatorId}`, { photo_path: data.path });
      onChange(data.path);
      toast.success(facilitatorId ? "Photo saved." : "Photo added. Click Add to keep it.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Photo upload failed.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4 mt-5" data-testid="facilitator-photo-picker">
      <div
        className="w-20 h-20 rounded-full bg-slate-100 bg-cover bg-center border-2 border-slate-200 flex items-center justify-center text-slate-400 shrink-0"
        style={path ? { backgroundImage: `url(${fileUrl(path)})` } : undefined}
        data-testid="facilitator-photo-preview"
      >
        {!path && <Camera size={22} />}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="facilitator-photo-input" />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="facilitator-photo-upload-btn" className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-tc-ink text-white text-sm font-bold hover:opacity-90 transition-opacity disabled:opacity-60">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
          {path ? "Change photo" : "Upload photo"}
        </button>
        {path && (
          <button type="button" onClick={() => onChange(null)} data-testid="facilitator-photo-remove-btn" className="text-sm font-bold text-slate-500 hover:text-red-600 transition-colors">Remove</button>
        )}
        <p className="w-full text-xs text-slate-500">JPG, PNG or WebP, under 8 MB.</p>
      </div>
    </div>
  );
}

// Keeps the raw text (spaces included) while typing; parses to a list for saving.
function ListInput({ value, onChange, testid, placeholder }) {
  const [text, setText] = useState((value || []).join(", "));
  return (
    <input
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean));
      }}
      data-testid={testid}
      className={INPUT}
      placeholder={placeholder}
    />
  );
}

function EditModal({ id, existing, onClose, onSaved }) {
  const [draft, setDraft] = useState(existing ? { ...existing, specialties: existing.specialties || [], languages: existing.languages || [] } : { ...empty });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isNew = id === "new";

  const updateField = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    setSaving(true);
    try {
      const body = { ...draft, email: draft.email?.trim() || null };
      if (isNew) await api.post("/facilitators", body);
      else await api.put(`/facilitators/${id}`, body);
      toast.success(isNew ? "Facilitator added." : "Saved.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm(`Delete ${draft.name}? This can't be undone.`)) return;
    try {
      await api.delete(`/facilitators/${id}`);
      toast.success("Deleted.");
      onSaved();
    } catch {
      toast.error("Delete failed.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="facilitator-modal"
    >
      <motion.div
        initial={{ y: 30, scale: 0.95, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        exit={{ y: 30, scale: 0.95, opacity: 0 }}
        className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-6 sm:p-7 max-h-[92vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500"><X size={16} /></button>
        <h2 className="font-display text-2xl text-tc-ink">{isNew ? "Add facilitator" : "Edit facilitator"}</h2>

        <PhotoPicker facilitatorId={isNew ? null : id} path={draft.photo_path} onChange={(p) => updateField("photo_path", p)} uploading={uploading} setUploading={setUploading} />

        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          <Field label="Full name" required><input value={draft.name} onChange={(e) => updateField("name", e.target.value)} className={INPUT} data-testid="facilitator-field-name" /></Field>
          <Field label="Email"><input type="email" value={draft.email || ""} onChange={(e) => updateField("email", e.target.value)} className={INPUT} data-testid="facilitator-field-email" /></Field>
          <Field label="Phone"><input value={draft.phone || ""} onChange={(e) => updateField("phone", e.target.value)} className={INPUT} data-testid="facilitator-field-phone" /></Field>
          <Field label="City"><input value={draft.city || ""} onChange={(e) => updateField("city", e.target.value)} className={INPUT} /></Field>
          <Field label="Country">
            <select value={draft.country || ""} onChange={(e) => { updateField("country", e.target.value); if (e.target.value !== "United States") updateField("state", ""); }} className={INPUT}>
              <option value="">Choose…</option>{COUNTRIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </Field>
          <Field label={draft.country === "United States" ? "State" : "State / Province"}>
            {draft.country === "United States"
              ? <select value={draft.state || ""} onChange={(e) => updateField("state", e.target.value)} className={INPUT}><option value="">Choose…</option>{US_STATES.map((s) => <option key={s}>{s}</option>)}</select>
              : <input value={draft.state || ""} onChange={(e) => updateField("state", e.target.value)} className={INPUT} />}
          </Field>
          <Field label="Specialties (comma-separated)" full>
            <ListInput value={draft.specialties} onChange={(v) => updateField("specialties", v)} testid="facilitator-specialties-input" placeholder="Leadership, Customer Service…" />
          </Field>
          <Field label="Languages (comma-separated)" full>
            <ListInput value={draft.languages} onChange={(v) => updateField("languages", v)} testid="facilitator-languages-input" placeholder="English, Spanish…" />
          </Field>
          <Field label="Bio" full>
            <textarea rows={5} value={draft.bio || ""} onChange={(e) => updateField("bio", e.target.value)} className={`${INPUT} h-auto py-2.5 leading-relaxed resize-none`} data-testid="facilitator-field-bio" />
          </Field>
          <Field label="Status" full>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={draft.available} onChange={(e) => updateField("available", e.target.checked)} className="w-5 h-5 rounded accent-purple-500" />
              <span className="text-sm font-bold text-tc-ink">Available for assignments</span>
            </label>
          </Field>
        </div>

        <div className="mt-6 pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
          {!isNew ? (
            <button onClick={remove} className="inline-flex items-center gap-1.5 px-3 h-10 rounded-xl text-red-600 hover:bg-red-50 font-bold text-sm" data-testid="facilitator-delete">
              <Trash2 size={14} /> Delete
            </button>
          ) : <div />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 h-10 rounded-xl font-bold text-slate-500 hover:bg-slate-100">Cancel</button>
            <button onClick={save} disabled={saving || uploading} data-testid="facilitator-save" className="px-4 h-10 rounded-xl bg-purple-600 text-white font-bold flex items-center gap-2 disabled:opacity-60">
              {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading photo</> : saving ? <><Loader2 size={14} className="animate-spin" /> Saving</> : <><Save size={14} /> {isNew ? "Add" : "Save"}</>}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

const INPUT = "w-full h-11 rounded-xl border-2 border-slate-200 focus:border-purple-500 outline-none px-3 text-sm";

function Field({ label, required, full, children }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="tc-eyebrow text-slate-500 mb-1.5 block">
        {label} {required && <span className="text-red-500 normal-case">*</span>}
      </span>
      {children}
    </label>
  );
}
